import { flushSync } from 'react-dom';
import type { NavigateFunction, To } from 'react-router-dom';
import type { MenuMode } from '@/hooks/usePlayer';
import { paths } from '@/lib/routes';

/** Modes that participate in horizontal swipe / slide view transitions. */
export const MODE_ORDER: MenuMode[] = ['music', 'photo', 'info', 'settings'];

export function modePath(mode: MenuMode): string {
  if (mode === 'photo') return paths.photo();
  if (mode === 'info') return paths.info();
  if (mode === 'settings') return paths.settings();
  return paths.music();
}

export function modeIndex(mode: MenuMode): number {
  return MODE_ORDER.indexOf(mode);
}

/** Set html data attribute so view-transition CSS can slide the right way. */
export function setViewTransitionDir(from: MenuMode, to: MenuMode) {
  if (typeof document === 'undefined' || from === to) return;
  const dir = modeIndex(to) > modeIndex(from) ? 'forward' : 'back';
  document.documentElement.dataset.vtDir = dir;
}

/** `forward`/`back` = catalog↔detail or leave-detail mode change (media fades).
 *  `mode-*` = list↔list tab switch (portrait stays put). */
export type ViewTransitionDir =
  | 'forward'
  | 'back'
  | 'up'
  | 'mode-forward'
  | 'mode-back';

/** Push / pop direction for catalog ↔ detail (iOS-style). */
export function setDetailTransitionDir(dir: 'forward' | 'back') {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.vtDir = dir;
}

/** Horizontal mode switch — catalog slides; media/portrait does not fade. */
export function modeSwitchDir(
  from: MenuMode,
  to: MenuMode,
): 'mode-forward' | 'mode-back' | undefined {
  if (from === to) return undefined;
  const fromI = modeIndex(from);
  const toI = modeIndex(to);
  if (fromI < 0 || toI < 0) return undefined;
  return toI > fromI ? 'mode-forward' : 'mode-back';
}

export function clearViewTransitionDir() {
  if (typeof document === 'undefined') return;
  delete document.documentElement.dataset.vtDir;
}

function canMorphWithViewTransition(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** In-flight View Transition — used to skip stacked morphs on rapid nav. */
let inFlightVt: ViewTransition | null = null;

/**
 * Catalog/detail marquees only. Mini tray + desktop mini chrome keep their
 * playhead — parking them made the now-playing title restart on every nav.
 */
function forEachParkableTitleTrack(fn: (el: HTMLElement) => void) {
  document.querySelectorAll('.scrolling-title-track').forEach((node) => {
    const el = node as HTMLElement;
    if (
      el.closest(
        '.now-playing-tray-vt, .now-playing-tray, .mini-player, .mini-now-playing',
      )
    ) {
      return;
    }
    fn(el);
  });
}

/**
 * Forward / mode-forward / up: snapshot page marquees at left-aligned rest.
 * Back / mode-back: leave playhead alone so the outgoing title stays mid-scroll.
 */
function parkScrollingTitlesAtRest() {
  forEachParkableTitleTrack((el) => {
    el.getAnimations().forEach((a) => {
      try {
        a.currentTime = 0;
        a.pause();
      } catch {
        /* ignore */
      }
    });
    el.style.transform = 'translate3d(0,0,0)';
  });
  // Ensure the rest transform is committed before startViewTransition captures.
  void document.documentElement.offsetHeight;
}

function resumeScrollingTitles(resetToRest: boolean) {
  forEachParkableTitleTrack((el) => {
    if (resetToRest) el.style.transform = '';
    el.getAnimations().forEach((a) => {
      try {
        if (a.playState === 'paused') {
          if (resetToRest) a.currentTime = 0;
          a.play();
        }
      } catch {
        /* ignore */
      }
    });
  });
  window.dispatchEvent(
    new CustomEvent('zg:vt-finished', {
      detail: { resetToRest, parkableOnly: true },
    }),
  );
}

function shouldParkTitlesAtRest(dir?: ViewTransitionDir): boolean {
  return dir === 'forward' || dir === 'mode-forward' || dir === 'up';
}

/**
 * BrowserRouter does not reliably honor `viewTransition: true`.
 * Wrap DOM updates in the View Transitions API ourselves.
 *
 * If a morph is already in flight, apply the update immediately (no morph)
 * so rapid clicks don’t stack overlapping old/new snapshots. Leave the
 * in-flight morph and its `data-vt-dir` alone until it finishes.
 */
export function runViewTransition(
  update: () => void,
  dir?: ViewTransitionDir,
): void {
  if (typeof document === 'undefined') {
    update();
    return;
  }

  if (inFlightVt) {
    flushSync(update);
    return;
  }

  if (!canMorphWithViewTransition()) {
    update();
    return;
  }

  if (dir) document.documentElement.dataset.vtDir = dir;
  else delete document.documentElement.dataset.vtDir;

  const parkAtRest = shouldParkTitlesAtRest(dir);
  if (parkAtRest) parkScrollingTitlesAtRest();

  const transition = document.startViewTransition(() => {
    flushSync(update);
  });
  inFlightVt = transition;
  void transition.finished.finally(() => {
    if (inFlightVt === transition) {
      inFlightVt = null;
      delete document.documentElement.dataset.vtDir;
      resumeScrollingTitles(parkAtRest);
    }
  });
}

export function navigateViewTransition(
  navigate: NavigateFunction,
  to: To,
  dir?: ViewTransitionDir,
  options?: { replace?: boolean },
): void {
  runViewTransition(() => {
    navigate(to, { replace: options?.replace });
  }, dir);
}

export function adjacentMode(
  mode: MenuMode,
  delta: -1 | 1,
): MenuMode | null {
  const i = modeIndex(mode) + delta;
  if (i < 0 || i >= MODE_ORDER.length) return null;
  return MODE_ORDER[i]!;
}
